import React, { useState, useEffect, useCallback } from 'react';
import { isDeliveryType, isPickupType, displayBookingType } from '../../../constants/bookingTypes';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import EditBooking from './EditBooking';
import { formatDubai } from '../../../utils/dubaiTime';
import './dailyBookings.css';
import { isMaintenanceTypeName } from './bookingTypeRules';
import { colorOf, AREA_PRIORITY, ROUTE_GROUPS } from '../../../constants/areas';

function DailyBookings({ customDate }) {
    const { date: routeDate } = useParams();
    const navigate = useNavigate();
    const date = customDate || routeDate;
    const [bookings, setBookings] = useState([]);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [loading, setLoading] = useState(true);

    const [routeDrivers, setRouteDrivers] = useState([]); // routes for the day, read-only

    // The far side of town first — one shared order, see constants/areas.js.
    const areaPriorityOrder = AREA_PRIORITY;


    const fetchBookings = useCallback(async () => {
        try {
            const response = await axios.get(`/api/bookings/daily-schedule/${date}`);
            setBookings(response.data.bookings);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching bookings:', error);
            setLoading(false);
        }
    }, [date]);

    /**
     * Driver assignment now lives ONLY on the Cleaner Scheduling page — routes
     * are real records there, any number of them, and this page used to write
     * to the old two-route /api/drivers/routes/* endpoints. Two places writing
     * the same thing meant setting a driver here left the scheduling board
     * unaware. This page now READS the routes and shows them; it never writes.
     */
    const fetchRouteDrivers = useCallback(async () => {
        if (!date) return;
        try {
            const { data } = await axios.get(`/api/routing/${date}/routes`);
            setRouteDrivers(data.routes || []);
        } catch (e) {
            console.error('Error fetching routes:', e);
            setRouteDrivers([]);
        }
    }, [date]);


    // Fetch bookings on date change or initial render
    useEffect(() => {
        (async () => {
                  // ensure Downtown & Marina routes exist
            await fetchBookings();     // bookings for the day
            await fetchRouteDrivers(); // current assigned drivers per route
        })();
    }, [fetchBookings, fetchRouteDrivers]);

    // Helper: Parse time to a float for easier comparisons
    const parseTime = useCallback((timeString) => {
        if (!timeString) return NaN;
        const timeMatch = timeString.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (!timeMatch) return NaN;
        let [, hours, minutes, period] = timeMatch;
        hours = parseInt(hours, 10);
        minutes = parseInt(minutes, 10);
        if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
        else if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
        return hours + minutes / 60;
    }, []);

    // Helper: Get area color
    const getAreaColor = (area) => {
        return colorOf(area);
    };

    // Render individual booking entry
    const renderBooking = (booking, areaColor, bookingNumber) => {
        const cleanerNames = booking.cleaner_names.length
            ? booking.cleaner_names.join(', ')
            : ' ';
        const linenHireText = (booking.is_linen_hire && booking.is_laundry) ? ' + Linen Hire' : '';
        const additionalInfo = [2, 3, 4].includes(booking.booking_type_id)
            ? `, Laundry: ${booking.is_laundry ? 'Yes' : 'No'}, Amenities: ${booking.is_amenities ? 'Yes' : 'No'
            }`
            : '';

        const typeBadge =
            isDeliveryType(booking.booking_type)
                ? `<span class="badge badge-orange">Delivery Only</span>`
                : isPickupType(booking.booking_type)
                    ? `<span class="badge badge-orange">Pickup Only</span>`
                    : booking.booking_type === 'Laundry Service Only'
                        ? `<span class="badge badge-laundry">Laundry</span>`
                        : booking.booking_type === 'Maintenance'
                            ? `<span class="badge badge-maintenance">Maintenance</span>`
                            : booking.booking_type === 'Swimming Pool Maintenance'
                                ? `<span class="badge badge-maintenance">Swimming Pool Maintenance</span>`
                                : '';

        const cancelledBadge =
            booking.status === 'Last Minute Cancelled' || booking.status === 'Last Minute Rescheduled'
                ? `<span class="badge badge-red">Cancelled</span>`
                : '';

        const pendingBadge =
            booking.status === 'Pending'
                ? `<span class="badge badge-pending">Pending</span>`
                : '';

        const completedBadge =
            booking.status === 'Completed'
                ? `<span class="badge badge-completed">Completed</span>`
                : '';

        const inProcessBadge =
            booking.status === "In Process"
                ? `<span class="badge badge-in-process">Cleaning Started..</span>`
                : '';

        return (
            <div
                key={booking.id}
                className="booking-entry"
                style={{ borderLeft: `5px solid ${areaColor}`, paddingLeft: '10px' }}
            >
                <h3>
                    <a
                        href={`/bookings/${booking.id}`}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                        onMouseOver={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                        onMouseOut={(e) => (e.currentTarget.style.textDecoration = 'none')}
                    >
                        {bookingNumber}. {booking.client_name} - {booking.building_name} - Unit {booking.unit_number} (
                        {booking.bedrooms === 0 ? 'S' : booking.bedrooms} - {booking.bathrooms})
                    </a>
                    <button
                        className="quick-edit-button"
                        onClick={() => {
                            setSelectedBooking(booking.id); // Set booking ID instead of full booking
                        }}
                        style={{ marginLeft: '10px', cursor: 'pointer' }}
                    >
                        <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                </h3>
                <p>
                    {/* Maintenance-like bookings never need cleaners — say so
                        instead of listing nobody (2026-10-04). */}
                    <strong>Cleaners:</strong>{' '}
                    {isMaintenanceTypeName(booking.booking_type)
                        ? `not needed — ${booking.booking_type === 'Swimming Pool Maintenance' ? 'Pool Maintenance' : 'Maintenance'}`
                        : cleanerNames}
                </p>
                <p>
                    <strong>Scheduling Note:</strong> {booking.scheduling_note}
                </p>
                <p>
                    <strong>Access:</strong> {booking.property_access || 'Not Provided'}
                </p>
                <p>
                    <strong>Start Time:</strong> {booking.start_time}
                </p>
                <p>
                    <strong>Type:</strong> {displayBookingType(booking.booking_type)}
                    {linenHireText} ({booking.duration} hours)
                    {additionalInfo}
                </p>
                <div dangerouslySetInnerHTML={{ __html: typeBadge + pendingBadge + cancelledBadge + completedBadge + inProcessBadge }} />
            </div >
        );
    };

    const renderCategory = (filteredBookings, categoryTitle, timeFilterFn) => {
        if (!filteredBookings.length) return null;

        // total required cleaners for this time group
        const categoryBookings = filteredBookings.filter(timeFilterFn);
        const totalCleaners = categoryBookings.reduce(
            (sum, b) => sum + (Number(b.required_cleaners) || 0),
            0
        );
        if (!categoryBookings.length) return null;

        // Group bookings by area (keep your existing layout)
        const bookingsByArea = filteredBookings.reduce((acc, booking) => {
            if (!acc[booking.area]) acc[booking.area] = [];
            acc[booking.area].push(booking);
            return acc;
        }, {});

        // Sort areas based on start time and priority order
        const sortedAreas = Object.keys(bookingsByArea).sort((a, b) => {
            const firstBookingA = bookingsByArea[a].find(timeFilterFn);
            const firstBookingB = bookingsByArea[b].find(timeFilterFn);

            if (!firstBookingA || !firstBookingB) return 0;

            const timeA = parseTime(firstBookingA.start_time);
            const timeB = parseTime(firstBookingB.start_time);

            if (timeA === timeB) {
                return areaPriorityOrder.indexOf(a) - areaPriorityOrder.indexOf(b);
            }
            return timeA - timeB;
        });

        let counter = 1;

        return (
            <div>
                <h4>
                    {categoryTitle}: <span><strong>{totalCleaners}</strong> cleaner{totalCleaners === 1 ? '' : 's'}</span>
                </h4>

                {sortedAreas.map((area) => {
                    const areaBookings = bookingsByArea[area].filter(timeFilterFn);
                    if (!areaBookings.length) return null;
                    return (
                        <div key={area}>
                            {areaBookings.map((booking) => {
                                const bookingElement = renderBooking(
                                    booking,
                                    getAreaColor(area),
                                    counter++
                                );
                                return <div key={booking.id}>{bookingElement}</div>;
                            })}
                        </div>
                    );
                })}
            </div>
        );
    };

    if (loading) return <div>Loading...</div>;

    // One list, shared with the router — see constants/areas.js. This copy was
    // also five areas short of the board's, so a JVT or Sports City booking
    // counted in neither group.
    const group1Locations = ROUTE_GROUPS.Downtown;
    const group2Locations = ROUTE_GROUPS.Marina;
    const laundryBookings = bookings.filter((b) => b.booking_type === 'Laundry Service Only');
    const cancelledBookings = bookings.filter(
        (b) => b.status === 'Last Minute Cancelled' || b.status === 'Last Minute Rescheduled'
    );

       return (
        <div className="daily-bookings-container">
            <div className="daily-bookings-header">
                <h2>
                    {/* Asia/Dubai render (item 8): the route day, not the
                        device's reading of UTC midnight */}
                    {formatDubai(date, 'cccc d LLLL yyyy')}
                    , Schedule
                </h2>
                <button
                    type="button"
                    className="daily-bookings-scheduling-button"
                    onClick={() => navigate(`/bookings/scheduling/${date}`)}
                >
                    Open Cleaner Scheduling
                </button>
            </div>

            {/* Drivers are assigned on the Cleaner Scheduling page; shown here
                so the printed daily schedule still carries them. */}
            {routeDrivers.length > 0 && (
                <div className="daily-bookings-routes">
                    {routeDrivers.map((r) => (
                        <span key={r.id} className="daily-bookings-route">
                            <strong>{r.route_name}:</strong>{' '}
                            {r.driver_first_name
                                ? `${r.driver_first_name} ${r.driver_last_name || ''}`.trim()
                                : 'No driver assigned'}
                        </span>
                    ))}
                </div>
            )}

            <div className="group">

                {renderCategory(
                    bookings.filter(
                        (b) =>
                            group1Locations.includes(b.area) &&
                            b.booking_type !== 'Laundry Service Only' &&
                            b.status !== 'Last Minute Cancelled' &&
                            b.status !== 'Last Minute Rescheduled'
                    ),
                    'Morning Cleanings',
                    (b) => parseTime(b.start_time) < 10
                )}
                {renderCategory(
                    bookings.filter(
                        (b) =>
                            group1Locations.includes(b.area) &&
                            b.booking_type !== 'Laundry Service Only' &&
                            b.status !== 'Last Minute Cancelled' &&
                            b.status !== 'Last Minute Rescheduled'
                    ),
                    'Peak Hour Cleanings',
                    (b) => parseTime(b.start_time) >= 10 && parseTime(b.start_time) < 14
                )}
                {renderCategory(
                    bookings.filter(
                        (b) =>
                            group1Locations.includes(b.area) &&
                            b.booking_type !== 'Laundry Service Only' &&
                            b.status !== 'Last Minute Cancelled' &&
                            b.status !== 'Last Minute Rescheduled'
                    ),
                    'Afternoon Cleanings',
                    (b) => parseTime(b.start_time) >= 14
                )}
            </div>

            <div className="group">
                {renderCategory(
                    bookings.filter(
                        (b) =>
                            group2Locations.includes(b.area) &&
                            b.booking_type !== 'Laundry Service Only' &&
                            b.status !== 'Last Minute Cancelled' &&
                            b.status !== 'Last Minute Rescheduled'
                    ),
                    'Morning Cleanings',
                    (b) => parseTime(b.start_time) < 10
                )}
                {renderCategory(
                    bookings.filter(
                        (b) =>
                            group2Locations.includes(b.area) &&
                            b.booking_type !== 'Laundry Service Only' &&
                            b.status !== 'Last Minute Cancelled' &&
                            b.status !== 'Last Minute Rescheduled'
                    ),
                    'Peak Hour Cleanings',
                    (b) => parseTime(b.start_time) >= 10 && parseTime(b.start_time) < 14
                )}
                {renderCategory(
                    bookings.filter(
                        (b) =>
                            group2Locations.includes(b.area) &&
                            b.booking_type !== 'Laundry Service Only' &&
                            b.status !== 'Last Minute Cancelled' &&
                            b.status !== 'Last Minute Rescheduled'
                    ),
                    'Afternoon Cleanings',
                    (b) => parseTime(b.start_time) >= 14
                )}
            </div>

            {laundryBookings.length > 0 && (
                <div className="group">
                    <h3 className='driver-title'>Laundry Service Only:</h3>
                    {laundryBookings.map((booking) =>
                        renderBooking(booking, getAreaColor(booking.area))
                    )}
                </div>
            )}

            {cancelledBookings.length > 0 && (
                <div className="group">
                    <h3 className='driver-title' >Cancelled / Rescheduled Bookings:</h3>
                    {cancelledBookings.map((booking) =>
                        renderBooking(booking, getAreaColor(booking.area))
                    )}
                </div>
            )}

            {selectedBooking && (
                <EditBooking
                    bookingId={selectedBooking} // Pass the booking ID
                    onClose={() => {
                        setSelectedBooking(null); // Close the popup
                        fetchBookings(); // Fetch bookings again
                    }}
                />
            )}
        </div>
    );
}

export default DailyBookings;